import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import { Auth } from 'aws-amplify';
import { CustomAuth } from './models/Entities';
import { ConfigProvider, message } from 'antd';
import authService from './services/AuthService';
import esES from 'antd/lib/locale/es_ES';
import appThemeComponent from './components/AppThemeComponent/AppThemeComponent';
import CustomContext from './contexts/CustomContext';
import './App.css';
import configuration from './configs/Configuration';
import { IntlProvider } from 'react-intl';
import messagesEs from './i18n/es.json';
import HeadMetadata from './components/HeadMetadata';
import RoutesComponent from './components/RoutesComponent/RoutesComponent';
import { App as AntdApp } from 'antd';
import notificationUserApi from './apis/NotificationUserApi';
import companyApi from './apis/CompanyApi';

// set up DayJs
dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(objectSupport);

// set up AWS authentication
Auth.configure(configuration.awsAuth);

// return the app

const App: React.FC = () => {
    /*** HOOKS ***/

    // create context
    const [auth, setAuth] = useState<CustomAuth>();
    const [notifications, setNotifications] = useState<number>(0);
    const [initialized, setInitialized] = useState<boolean>(false);

    // sync GA4 user identity whenever auth changes
    useEffect(() => {
        if (auth?.initialized && auth.id) {
            // authenticated — attach user identity to all subsequent hits
            const syncIdentity = async () => {
                ReactGA.set({ userId: String(auth.id) });

                const userProperties: Record<string, string | undefined> = {
                    principal_id: String(auth.id),
                    company_id: auth.companyId ? String(auth.companyId) : undefined,
                    company_name: undefined,
                };

                if (auth.companyId) {
                    try {
                        const company = await companyApi.get(auth.companyId);
                        userProperties.company_name = company.name;
                    } catch (e) {
                        console.error('Failed to fetch company name for GA4 tracking', e);
                    }
                }

                ReactGA.gtag('set', 'user_properties', userProperties);
            };
            syncIdentity();
        } else if (auth !== undefined) {
            // auth check completed but no user logged in (or just logged out) — clear identity
            ReactGA.set({ userId: undefined });
            ReactGA.gtag('set', 'user_properties', {
                principal_id: undefined,
                company_id: undefined,
                company_name: undefined,
            });
        }
        // auth === undefined means the app is still initializing — do nothing yet
    }, [auth]);

    // initialize the app
    useEffect(() => {
        const init = async () => {
            // set up antd configuration and locale
            message.config({ maxCount: 1 });
            dayjs.locale('es');

            const auth = await authService.get();
            const notifications = await countNotificiations(auth);

            // store deep link
            storeDeepLink(auth);

            setAuth(auth);
            setNotifications(notifications);
            setInitialized(true);
        };
        init();
    }, []);

    /*** METHODS ***/

    const countNotificiations = async (auth?: CustomAuth): Promise<number> => {
        let notifications: number = 0;
        if (auth && auth.initialized) {
            notifications = await notificationUserApi.countRead(false);
        }

        return notifications;
    };

    const updateContextAuth = (auth?: CustomAuth): void => {
        setAuth(auth);
    };

    const updateContextNotifications = (notifications: number): void => {
        setNotifications(notifications);
    };

    /*** VISUAL ***/

    if (initialized) {
        return (
            <ConfigProvider locale={esES} theme={appThemeComponent.getAppTheme()}>
                <AntdApp>
                    <CustomContext.Provider value={{ auth, setAuth: updateContextAuth, notifications, setNotifications: updateContextNotifications }}>
                        <IntlProvider locale="es-ES" defaultLocale="es-ES" messages={messagesEs}>
                            <HeadMetadata />
                            <RoutesComponent />
                        </IntlProvider>
                    </CustomContext.Provider>
                </AntdApp>
            </ConfigProvider>
        );
    } else {
        return <></>;
    }
};

export default App;

/**
 * Stores the deep link if the user is not authenticated or not initialized.
 * @param auth the auth
 * @returns the current deep link
 */
const storeDeepLink = (auth: CustomAuth | undefined) => {
    if (!auth || !auth.initialized) {
        sessionStorage.setItem('deeplink', window.location.pathname + window.location.search);
    }
};
